import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ImageScratchCard } from '@/components/PurchaseScratchCard/ImageScratchCard'
import type { usePurchaseScratch } from '@/hooks/usePurchaseScratch'
import type { LandingSpinResult } from '@/lib/landing-roulette'
import { scratchError, type PurchaseScratchAttempt } from '@/lib/purchase-scratch'
import { PurchaseScratchHistory } from './PurchaseScratchHistory'
import { PurchaseScratchResult } from './PurchaseScratchResult'
import styles from '@/components/PurchaseScratchCard/PurchaseScratchCard.module.css'

type Props = {
  campaignId: number
  donorId: number
  scratch: ReturnType<typeof usePurchaseScratch>
  onChanged: () => void
  onUseCoupon: (prize: LandingSpinResult) => Promise<void>
  onViewNumbers: () => void
  onPurchase: () => Promise<boolean | void>
  purchaseLoading: boolean
  salesOpen: boolean
}

export function PurchaseScratchModal({
  campaignId,
  donorId,
  scratch,
  onChanged,
  onUseCoupon,
  onViewNumbers,
  onPurchase,
  purchaseLoading,
  salesOpen,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const rewardDialog = useRef<HTMLDialogElement>(null)
  const [celebrate, setCelebrate] = useState(false)
  const [visuallyRevealedId, setVisuallyRevealedId] = useState<number | null>(null)
  const [using, setUsing] = useState(false)
  const checkoutLock = useRef(false)
  const alive = useRef(false)
  const [useError, setUseError] = useState('')
  const [result, setResult] = useState<PurchaseScratchAttempt | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const { attempt, state, busy, error, open } = scratch
  const isOpen = useRef(open)
  const visuallyRevealed = Boolean(attempt && visuallyRevealedId === attempt.id)
  const imageOnly = Boolean(
    attempt && !visuallyRevealed && !attempt.revealed_at && !error
  )
  const loading = busy && (!attempt || Boolean(attempt.revealed_at))
  const minimal = loading || imageOnly
  const canDonate = Boolean(state?.enabled && salesOpen)
  const checkoutBusy = using || purchaseLoading

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  useEffect(() => {
    isOpen.current = open
    const element = dialog.current
    if (open && element && !element.open) element.showModal()
    if (!open) {
      if (element?.open) element.close()
      setResult(null)
    }
  }, [open])
  useEffect(() => {
    setVisuallyRevealedId(null)
    setUseError('')
    setCelebrate(false)
  }, [attempt?.id])

  async function reveal() {
    setVisuallyRevealedId(attempt?.id ?? null)
    const card = await scratch.reveal()
    if (card && alive.current) {
      onChanged()
      if (dialog.current?.open) {
        setCelebrate(card.won && card.prize.status !== 'canceled')
        setResult(card)
      }
    }
  }

  async function checkout(task: () => Promise<boolean | void>) {
    if (checkoutLock.current || purchaseLoading) return
    checkoutLock.current = true
    setUsing(true)
    setUseError('')
    const restoreResult = Boolean(result)
    // Native dialogs must leave the top layer before opening the existing donation widget.
    rewardDialog.current?.close()
    dialog.current?.close()
    try {
      const opened = await task()
      if (opened === false)
        throw new Error('Não foi possível abrir a doação agora. Tente novamente.')
      if (alive.current) {
        setResult(null)
        scratch.close()
      }
    } catch (cause) {
      if (alive.current && isOpen.current) {
        setUseError(scratchError(cause))
        dialog.current?.showModal()
        if (restoreResult) rewardDialog.current?.showModal()
      }
    } finally {
      checkoutLock.current = false
      if (alive.current) setUsing(false)
    }
  }
  function donate() {
    if (canDonate) void checkout(onPurchase)
  }
  function next() {
    setResult(null)
    void scratch.start()
  }
  const donationButton = (
    <div className={styles.donationPrompt}>
      {state?.enabled && (
        <>
          <p>Uma nova doação paga libera mais uma raspadinha para você raspar aqui.</p>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={!salesOpen || checkoutBusy || busy}
            onClick={donate}
          >
            <Icon icon={checkoutBusy ? 'mdi:loading' : 'mdi:heart-plus-outline'} />{' '}
            {checkoutBusy ? 'Abrindo doação…' : 'Doar e ganhar uma raspadinha'}
          </button>
          {!salesOpen && <p>As doações desta campanha estão encerradas.</p>}
        </>
      )}
    </div>
  )

  return (
    <>
      <dialog
        ref={dialog}
        autoFocus
        tabIndex={-1}
        className={`${styles.modal} ${minimal ? styles.imageOnlyModal : ''}`}
        style={
          !loading && imageOnly && attempt
            ? {
                width: `min(540px, calc(100vw - 24px), calc((100dvh - 32px) * ${attempt.cover.width / attempt.cover.height}))`,
              }
            : undefined
        }
        aria-label={minimal ? 'Sua raspadinha' : undefined}
        aria-labelledby={minimal ? undefined : 'purchase-scratch-title'}
        onCancel={(event) => {
          event.preventDefault()
          if (!using) scratch.close()
        }}
        onClick={(event) => {
          if (minimal && event.target === event.currentTarget && !using) scratch.close()
        }}
      >
        <div className={minimal ? styles.imageOnlyBody : styles.modalBody}>
          {!minimal && (
            <header className={styles.modalHeader}>
              <div>
                <span className={styles.eyebrow}>SUA COMPRA GUARDOU UMA SURPRESA</span>
                <h2 id="purchase-scratch-title">
                  {attempt?.title || state?.title || 'Sua raspadinha'}
                </h2>
                <p>{attempt?.description || state?.description}</p>
              </div>
              <button
                type="button"
                className={styles.close}
                aria-label="Fechar raspadinha"
                disabled={using}
                onClick={scratch.close}
              >
                <Icon icon="mdi:close" />
              </button>
            </header>
          )}
          {loading ? (
            <div
              className={styles.centeredLoading}
              role="status"
              aria-label="Preparando sua raspadinha"
            >
              <span className={styles.loadingSpinner} aria-hidden />
            </div>
          ) : attempt ? (
            <>
              <ImageScratchCard
                key={attempt.id}
                cover={attempt.cover}
                result={attempt.result_image}
                area={attempt.area}
                revealed={visuallyRevealed || Boolean(attempt.revealed_at)}
                onReveal={() => void reveal()}
                imageOnly={imageOnly}
              />
              {!imageOnly && (
                <p className={styles.receipt}>
                  Raspadinha #{attempt.id} · Compra #{attempt.payment_id}
                </p>
              )}
              {busy && !imageOnly && <p role="status">Registrando a revelação…</p>}
              {visuallyRevealed && !attempt.revealed_at && !busy && (
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => void reveal()}
                >
                  Confirmar revelação
                </button>
              )}
              {attempt.revealed_at && (
                <>
                  <div className={styles.modalFooter}>
                    <button
                      type="button"
                      className={styles.primaryButton}
                      onClick={() => {
                        setCelebrate(false)
                        setResult(attempt)
                      }}
                    >
                      Ver resultado da raspadinha
                    </button>
                    {Boolean(state?.available) && (
                      <button
                        type="button"
                        className={styles.quietButton}
                        disabled={busy}
                        onClick={next}
                      >
                        Próxima raspadinha · {state?.available}
                      </button>
                    )}
                  </div>
                  {!state?.available && donationButton}
                </>
              )}
            </>
          ) : (
            <div className={styles.empty}>
              {state?.cover ? (
                <img
                  src={state.cover.url}
                  alt="Capa da raspadinha"
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 14 }}
                />
              ) : (
                <Icon icon="mdi:ticket-confirmation-outline" />
              )}
              <p>{state?.message || 'Consulte suas raspadinhas disponíveis.'}</p>
              <strong>Nenhuma raspadinha disponível agora.</strong>
              {donationButton}
            </div>
          )}
          {error && (
            <div className={styles.error} role="alert">
              {error}
              <button
                type="button"
                className={styles.quietButton}
                disabled={busy}
                onClick={() =>
                  void (attempt && !attempt.revealed_at ? reveal() : scratch.start())
                }
              >
                Tentar novamente
              </button>
            </div>
          )}
          {useError && !result && (
            <p className={styles.error} role="alert">
              {useError}
            </p>
          )}
          {!minimal && !busy && (
            <details
              open={historyOpen}
              className={styles.historyDetails}
              onToggle={(event) => setHistoryOpen(event.currentTarget.open)}
            >
              <summary>Meu histórico de raspadinhas</summary>
              {historyOpen && open && (
                <PurchaseScratchHistory
                  key={`${campaignId}:${donorId}:${state?.history[0]?.id ?? 0}`}
                  campaignId={campaignId}
                  onView={(card) => {
                    setCelebrate(false)
                    setUseError('')
                    setResult(card)
                  }}
                />
              )}
            </details>
          )}
        </div>
      </dialog>
      {result && open && (
        <PurchaseScratchResult
          key={result.id}
          card={result}
          dialogRef={rewardDialog}
          celebrate={celebrate}
          busy={checkoutBusy}
          error={useError}
          salesOpen={salesOpen}
          hasNext={Boolean(state?.available)}
          canDonate={canDonate}
          onClose={() => setResult(null)}
          onUseCoupon={() =>
            void checkout(() =>
              onUseCoupon({
                position: 0,
                prize_type: 'coupon',
                prize_payload: result.prize.prize_payload,
              })
            )
          }
          onViewNumbers={() => {
            setResult(null)
            scratch.close()
            onViewNumbers()
          }}
          onNext={next}
          onDonate={donate}
        />
      )}
    </>
  )
}
